import {
  BARREL_LEN,
  CLIMB,
  DEPTH_SLAB,
  DRONE_MAX_Z,
  FALL_DMG_PER_PX,
  FALL_DURATION,
  FALL_SAFE,
  FRAME_EVERY,
  MAX_SHOT_TIME,
  MAX_SPEED,
  SIM_DT,
  SLIDE_ANIM,
  TANK_CENTER_DY,
  TANK_HIT_R,
  WATER_Y,
  WIND_ACCEL,
  WORLD_W,
} from './constants';
import { makeOp, type Terrain } from './terrain';
import type { Mine, Tank, TurnEvents, TurnInput } from './types';
import {
  KAMIKAZE_CLIMB,
  KAMIKAZE_FUEL,
  KAMIKAZE_LAUNCH,
  KAMIKAZE_MAX_STEPS,
  KAMIKAZE_RESPONSE,
  KAMIKAZE_SPEED,
  KAMIKAZE_STRAFE,
  KAMIKAZE_TURN_COS,
  KAMIKAZE_TURN_COS_HALF,
  KAMIKAZE_TURN_SIN,
  KAMIKAZE_TURN_SIN_HALF,
  AIRSTRIKE_BOMBS,
  AIRSTRIKE_SPACING,
  BANANA_COUNT,
  BANANA_DAMAGE,
  BANANA_RADIUS,
  CLUSTER_COUNT,
  DIGGER_SPACING,
  DIGGER_STEPS,
  FLAME_R,
  GRENADE_FUSE,
  LASER_CARVE_EVERY,
  LASER_RANGE,
  LASER_SPEED,
  NAPALM_BURN,
  NAPALM_DPS,
  NAPALM_FLAMES,
  NAPALM_FLOW,
  ROLLER_TIME,
  BLACKHOLE_DURATION,
  BLACKHOLE_RANGE,
  BUNKER_DEPTH,
  BUNKER_SPEED,
  LIGHTNING_WIDTH,
  MINE_TRIGGER,
  QUAKE_DAMAGE,
  QUAKE_DURATION,
  SHEEP_SPEED,
  SHEEP_TIME,
  SHOTGUN_LIFE,
  SHOTGUN_PELLETS,
  SHOTGUN_SPREAD,
  GUIDED_COS,
  GUIDED_FUEL,
  GUIDED_MAX_STEPS,
  GUIDED_SIN,
  GUIDED_SPEED,
  BOMBER_ALTITUDE,
  BOMBER_BOMBS,
  BOMBER_COOLDOWN,
  BOMBER_MAX_STEPS,
  BOMBER_SPEED,
  WEAPONS,
  type ProjectileKind,
} from './weapons';

const EDGE = 8;

/** Jízda tanku o `steps` pixelů daným směrem. Čistě celočíselná, takže náhled v klientovi sedí se serverem. */
export function walk(terrain: Terrain, x: number, y: number, dir: -1 | 1, steps: number) {
  while (y > 0 && terrain.solid(x, y - 1)) y--;
  while (y < WATER_Y && !supported(terrain, x, y)) y++;
  const path: number[] = [];
  let drowned = false;
  for (let i = 0; i < steps; i++) {
    const nx = x + dir;
    if (nx < EDGE || nx > WORLD_W - 1 - EDGE) break;
    let ny: number;
    if (terrain.solid(nx, y - 1)) {
      let found = -1;
      for (let k = 1; k <= CLIMB; k++) {
        if (!terrain.solid(nx, y - 1 - k)) {
          found = k;
          break;
        }
      }
      if (found < 0) break;
      ny = y - found;
    } else {
      ny = y;
      while (ny < WATER_Y && !terrain.solid(nx, ny)) ny++;
    }
    x = nx;
    y = ny;
    path.push(x, y);
    if (y >= WATER_Y) {
      drowned = true;
      break;
    }
  }
  return { x, y, path, drowned };
}

function supported(terrain: Terrain, x: number, y: number): boolean {
  for (let dx = -5; dx <= 5; dx++) if (terrain.solid(x + dx, y)) return true;
  return false;
}

/** Nechá tank spadnout, dokud nemá oporu, a vynoří ho, pokud uvízl pod terénem. Vrací novou výšku (≥ WATER_Y = utopený). */
export function settleY(terrain: Terrain, x: number, y: number): number {
  while (y > 0 && terrain.solid(x, y - 1)) y--;
  while (y < WATER_Y && !supported(terrain, x, y)) y++;
  return y;
}

export function tankCenter(t: Pick<Tank, 'x' | 'y'>) {
  return { x: t.x, y: t.y - TANK_CENTER_DY };
}

export function barrelTip(t: Pick<Tank, 'x' | 'y'>, angle: number) {
  const a = (angle * Math.PI) / 180;
  const c = tankCenter(t);
  return { x: c.x + Math.cos(a) * BARREL_LEN, y: c.y - Math.sin(a) * BARREL_LEN };
}

export type GuidedStatus = 'flying' | 'hit' | 'water' | 'out';

/**
 * Let řízené střely – jediný zdroj pravdy pro klienta (živé řízení) i server
 * (přepočet ze záznamu řízení). Jen základní aritmetika a literálové konstanty,
 * takže oba dojdou ke stejné dráze.
 */
export class GuidedFlight {
  x: number;
  y: number;
  vx: number;
  vy: number;
  age = 0;
  steps = 0;
  status: GuidedStatus = 'flying';
  private readonly cx: number;
  private readonly cy: number;

  constructor(
    private readonly terrain: Terrain,
    private readonly tanks: readonly Pick<Tank, 'id' | 'x' | 'y' | 'alive'>[],
    private readonly shooter: Pick<Tank, 'id' | 'x' | 'y'>,
    angle: number,
  ) {
    const tip = barrelTip(shooter, angle);
    const a = (angle * Math.PI) / 180;
    this.x = tip.x;
    this.y = tip.y;
    this.vx = Math.cos(a) * GUIDED_SPEED;
    this.vy = -Math.sin(a) * GUIDED_SPEED;
    const c = tankCenter(shooter);
    this.cx = c.x;
    this.cy = c.y;
    // Zakopaný tank → výbuch hned u hlavně
    if (terrain.solid(c.x, c.y - 3)) this.status = 'hit';
  }

  /** Zbývající palivo 0–1 (bez paliva už nejde řídit a střela padá). */
  get fuel(): number {
    return Math.max(0, 1 - this.age / GUIDED_FUEL);
  }

  /** Jeden simulační krok. dir: −1 = zatočit doleva (proti směru hodinek), 1 = doprava. */
  step(dir: -1 | 0 | 1): GuidedStatus {
    if (this.status !== 'flying') return this.status;
    this.age += SIM_DT;
    this.steps++;
    if (this.age <= GUIDED_FUEL) {
      if (dir !== 0) {
        // Rotace v souřadnicích s osou y dolů: kladný úhel = po směru hodinek
        const s = dir * GUIDED_SIN;
        const vx = this.vx * GUIDED_COS - this.vy * s;
        const vy = this.vx * s + this.vy * GUIDED_COS;
        this.vx = vx;
        this.vy = vy;
      }
    } else {
      this.vy += this.terrain.gravity * SIM_DT;
    }
    const nx = this.x + this.vx * SIM_DT;
    const ny = this.y + this.vy * SIM_DT;
    this.x = nx;
    this.y = ny;
    if (nx < -60 || nx > WORLD_W + 60 || ny < -600 || this.steps >= GUIDED_MAX_STEPS) return (this.status = 'out');
    if (ny >= WATER_Y) {
      this.y = WATER_Y;
      return (this.status = 'water');
    }
    for (const t of this.tanks) {
      if (!t.alive) continue;
      if (t.id === this.shooter.id && this.age < 0.1) continue;
      const c = t.id === this.shooter.id ? { x: this.cx, y: this.cy } : tankCenter(t);
      if (Math.hypot(c.x - nx, c.y - ny) < TANK_HIT_R) return (this.status = 'hit');
    }
    const clear = Math.hypot(nx - this.cx, ny - this.cy) > BARREL_LEN + 10;
    if (clear && this.terrain.solid(nx, ny)) return (this.status = 'hit');
    return this.status;
  }
}

/** Vstup kamikadze dronu – stejné osy jako průzkumný dron, každá −1 … 1. */
export interface KamikazeInput {
  /** −1 dolů … 1 nahoru */
  throttle: number;
  /** −1 doleva … 1 doprava (natočení) */
  yaw: number;
  /** −1 dozadu … 1 dopředu */
  forward: number;
  /** −1 doleva … 1 doprava (úkrok) */
  strafe: number;
}

/** Osa −1 … 1 → úroveň 0–4 (−1, −½, 0, ½, 1). */
function level(v: number): number {
  const c = Math.max(-1, Math.min(1, v || 0));
  return Math.round(c * 2) + 2;
}

/** Vstup → povel 0–624 (4 osy po 5 úrovních), který se zaznamená a pošle serveru. */
export function encodeKamikaze(inp: KamikazeInput): number {
  return level(inp.throttle) + 5 * level(inp.yaw) + 25 * level(inp.forward) + 125 * level(inp.strafe);
}

/** Povel 0–624 → úrovně os (−1, −0.5, 0, 0.5, 1). */
export function decodeKamikaze(code: number): KamikazeInput {
  const d = (n: number) => ((Math.floor(code / n) % 5) - 2) / 2;
  return { throttle: d(1), yaw: d(5), forward: d(25), strafe: d(125) };
}

/** Povel „nic nedělat“ (dron visí). */
export const KAMIKAZE_IDLE = encodeKamikaze({ throttle: 0, yaw: 0, forward: 0, strafe: 0 });

/**
 * Let kamikadze dronu ve 3D – sdílený klientem (živé řízení) i serverem
 * (přepočet ze záznamu). Souřadnice jako 2D svět (x doprava, y dolů) + z do
 * hloubky; směr (hx, hz) je jednotkový vektor, yaw 0 = směr +x. Otáčí se jen
 * předpočítanými literály, takže oba dojdou ke stejné dráze.
 */
export class KamikazeFlight {
  x: number;
  y: number;
  z = 0;
  vx: number;
  vy: number;
  vz = 0;
  /** Směr letu (jednotkový vektor v rovině x–z). */
  hx: number;
  hz = 0;
  age = 0;
  steps = 0;
  status: GuidedStatus = 'flying';
  private readonly cx: number;
  private readonly cy: number;

  constructor(
    private readonly terrain: Terrain,
    private readonly tanks: readonly Pick<Tank, 'id' | 'x' | 'y' | 'alive'>[],
    private readonly shooter: Pick<Tank, 'id' | 'x' | 'y'>,
    angle: number,
    private readonly wind: number,
  ) {
    const tip = barrelTip(shooter, angle);
    const a = (angle * Math.PI) / 180;
    this.x = tip.x;
    this.y = tip.y;
    this.vx = Math.cos(a) * KAMIKAZE_LAUNCH;
    this.vy = -Math.sin(a) * KAMIKAZE_LAUNCH;
    this.hx = angle <= 90 ? 1 : -1;
    const c = tankCenter(shooter);
    this.cx = c.x;
    this.cy = c.y;
    if (terrain.solid(c.x, c.y - 3)) this.status = 'hit';
  }

  /** Natočení (rad) jako u průzkumného dronu – jen pro zobrazení. */
  get yaw(): number {
    return Math.atan2(-this.hz, this.hx);
  }

  /** Zbývající baterie 0–1. */
  get fuel(): number {
    return Math.max(0, 1 - this.age / KAMIKAZE_FUEL);
  }

  /** Odpálení na povel. */
  detonate() {
    if (this.status === 'flying') this.status = 'hit';
  }

  step(code: number): GuidedStatus {
    if (this.status !== 'flying') return this.status;
    this.age += SIM_DT;
    this.steps++;
    if (this.age <= KAMIKAZE_FUEL) {
      const inp = decodeKamikaze(code);
      if (inp.yaw !== 0) {
        // yaw > 0 = doprava = po směru hodinek při pohledu shora (jako DroneFlight)
        const half = inp.yaw === 0.5 || inp.yaw === -0.5;
        const c = half ? KAMIKAZE_TURN_COS_HALF : KAMIKAZE_TURN_COS;
        const s = (inp.yaw > 0 ? 1 : -1) * (half ? KAMIKAZE_TURN_SIN_HALF : KAMIKAZE_TURN_SIN);
        const hx = this.hx * c - this.hz * s;
        const hz = this.hx * s + this.hz * c;
        const len = Math.sqrt(hx * hx + hz * hz);
        this.hx = hx / len;
        this.hz = hz / len;
      }
      const tx = this.hx * inp.forward * KAMIKAZE_SPEED - this.hz * inp.strafe * KAMIKAZE_STRAFE;
      const tz = this.hz * inp.forward * KAMIKAZE_SPEED + this.hx * inp.strafe * KAMIKAZE_STRAFE;
      const ty = -inp.throttle * KAMIKAZE_CLIMB;
      const k = KAMIKAZE_RESPONSE * SIM_DT;
      this.vx += (tx - this.vx) * k + this.wind * WIND_ACCEL * 0.25 * SIM_DT;
      this.vy += (ty - this.vy) * k;
      this.vz += (tz - this.vz) * k;
    } else {
      this.vy += this.terrain.gravity * SIM_DT;
    }
    const nx = this.x + this.vx * SIM_DT;
    const ny = this.y + this.vy * SIM_DT;
    const nz = Math.max(-DRONE_MAX_Z, Math.min(DRONE_MAX_Z, this.z + this.vz * SIM_DT));
    this.x = nx;
    this.y = ny;
    this.z = nz;
    if (nx < -300 || nx > WORLD_W + 300 || ny < -600 || this.steps >= KAMIKAZE_MAX_STEPS) return (this.status = 'out');
    if (ny >= WATER_Y) {
      this.y = WATER_Y;
      return (this.status = 'water');
    }
    for (const t of this.tanks) {
      if (!t.alive) continue;
      if (t.id === this.shooter.id && this.age < 0.4) continue;
      const c = t.id === this.shooter.id ? { x: this.cx, y: this.cy } : tankCenter(t);
      const dx = c.x - nx;
      const dy = c.y - ny;
      const r = TANK_HIT_R + 2;
      if (dx * dx + dy * dy + nz * nz < r * r) return (this.status = 'hit');
    }
    const clear = Math.hypot(nx - this.cx, ny - this.cy) > BARREL_LEN + 10;
    if (clear && nz < DEPTH_SLAB && nz > -DEPTH_SLAB && this.terrain.solid(nx, ny)) return (this.status = 'hit');
    return this.status;
  }
}

export class BomberFlight {
  x: number;
  y: number;
  vx: number;
  vy = 0;
  dir: 1 | -1;
  steps = 0;
  age = 0;
  status: GuidedStatus = 'flying';
  bombsLeft = BOMBER_BOMBS;
  lastDropTime = -BOMBER_COOLDOWN;
  readonly droppedBombs: { x: number; y: number; vx: number; vy: number; dropStep: number; t: number }[] = [];

  constructor(angle: number) {
    this.dir = angle > 90 ? -1 : 1;
    this.x = this.dir === 1 ? -160 : WORLD_W + 160;
    this.y = BOMBER_ALTITUDE;
    this.vx = this.dir * BOMBER_SPEED;
    this.vy = 0;
  }

  step(drop: boolean | number): boolean {
    if (this.status !== 'flying') return false;
    this.steps++;
    this.age += SIM_DT;
    this.x += this.vx * SIM_DT;

    let dropped = false;
    if (drop && this.bombsLeft > 0 && this.age - this.lastDropTime >= BOMBER_COOLDOWN) {
      this.bombsLeft--;
      this.lastDropTime = this.age;
      this.droppedBombs.push({
        x: this.x,
        y: this.y + 16,
        vx: this.vx * 0.85,
        vy: 45,
        dropStep: this.steps,
        t: this.age,
      });
      dropped = true;
    }

    if ((this.dir === 1 && this.x > WORLD_W + 180) || (this.dir === -1 && this.x < -180) || this.steps >= BOMBER_MAX_STEPS) {
      this.status = 'out';
    }
    return dropped;
  }
}

/** Vypočte dopadové souřadnice bomby svržené v daný okamžik pro zaměřovač bombometčíka. */
export function predictBombImpact(
  terrain: Terrain,
  x0: number,
  y0: number,
  vx0: number,
  vy0: number,
  wind: number,
): { x: number; y: number } {
  let x = x0;
  let y = y0;
  let vx = vx0;
  let vy = vy0;
  const dt = 0.035;
  for (let i = 0; i < 70; i++) {
    vx += wind * WIND_ACCEL * dt;
    vy += terrain.gravity * dt;
    const nx = x + vx * dt;
    const ny = y + vy * dt;
    if (ny >= WATER_Y) return { x: round1(nx), y: WATER_Y };
    if (terrain.solid(nx, ny)) return { x: round1(nx), y: round1(ny) };
    x = nx;
    y = ny;
  }
  return { x: round1(x), y: round1(Math.min(WATER_Y, terrain.surfaceY(x))) };
}

/** Čte záznam řízení kamikadze dronu; po konci záznamu vrací null (= odpálit). */
export function steerRuns(steer: TurnInput['steer']): () => number | null {
  let run = 0;
  let left = steer?.[0]?.[0] ?? 0;
  return () => {
    while (steer && run < steer.length && left <= 0) {
      run++;
      left = steer[run]?.[0] ?? 0;
    }
    if (!steer || run >= steer.length) return null;
    left--;
    return steer[run][1];
  };
}

/** Čte záznam řízení krok po kroku; po jeho konci vrací 0. */
export function steerReader(steer: TurnInput['steer']): () => -1 | 0 | 1 {
  let run = 0;
  let left = steer?.[0]?.[0] ?? 0;
  return () => {
    while (steer && run < steer.length && left <= 0) {
      run++;
      left = steer[run]?.[0] ?? 0;
    }
    if (!steer || run >= steer.length) return 0;
    left--;
    return steer[run][1] as -1 | 0 | 1;
  };
}

const MAX_MINES = 30;

/**
 * Výbuchy, sesuvy, zranění a miny nad jedním tahem. Používá ho simulace
 * výstřelu i resolveTurn (miny po usazení tanků). `last` = čas poslední události.
 */
export function createBlaster(terrain: Terrain, tanks: Tank[], ev: TurnEvents, mines: Mine[], turn: number) {
  const b = {
    last: 0,
    hurt(tank: Tank, amount: number, at: number, by?: string) {
      if (!tank.alive || amount <= 0) return;
      tank.hp = Math.max(0, tank.hp - amount);
      ev.damage.push({ t: at, id: tank.id, amount, ...(by ? { by } : {}) });
      if (tank.hp <= 0) {
        tank.alive = false;
        ev.deaths.push({ t: at, id: tank.id, cause: 'hp', ...(by ? { by } : {}) });
      }
    },
    slide(x: number, y: number, r: number, at: number) {
      const op = makeOp(2, x, y, r);
      terrain.apply(op);
      ev.ops.push({ t: at, op });
      b.last = Math.max(b.last, at + SLIDE_ANIM);
    },
    /** z = hloubka výbuchu (kamikadze dron ve 3D); mimo plát terénu nevznikne kráter. */
    explode(x: number, y: number, r: number, dmg: number, at: number, withSlide = true, by?: string, z = 0) {
      const inSlab = z < DEPTH_SLAB && z > -DEPTH_SLAB;
      if (inSlab) {
        const op = makeOp(0, x, y, r);
        terrain.apply(op);
        ev.ops.push({ t: at, op });
        ev.explosions.push({ t: at, x: op[1], y: op[2], r: op[3], ...(z ? { z: round1(z) } : {}) });
        if (withSlide) b.slide(x, y, r, at);
      } else {
        ev.explosions.push({ t: at, x: round1(x), y: round1(y), r: Math.round(r), z: round1(z) });
      }
      b.last = Math.max(b.last, at);
      if (dmg > 0) {
        for (const tank of tanks) {
          if (!tank.alive) continue;
          const c = tankCenter(tank);
          const d = z ? Math.sqrt((c.x - x) ** 2 + (c.y - y) ** 2 + z * z) : Math.hypot(c.x - x, c.y - y);
          const R = r + TANK_HIT_R;
          if (d >= R) continue;
          b.hurt(tank, Math.max(1, Math.round(dmg * (1 - (d / R) ** 2))), at, by);
        }
      }
      // Řetězová reakce: výbuch odpálí miny v dosahu
      for (const m of mines.slice()) {
        if ((z ? Math.hypot(m.x - x, m.y - y, z) : Math.hypot(m.x - x, m.y - y)) < r + 4) b.detonate(m, at + 0.12);
      }
    },
    detonate(m: Mine, at: number) {
      const i = mines.indexOf(m);
      if (i < 0) return;
      mines.splice(i, 1);
      (ev.mines ??= []).push({ t: at, id: m.id, x: m.x, y: m.y, kind: 'gone' });
      b.explode(m.x, m.y - 2, WEAPONS.mine.radius, WEAPONS.mine.damage, at, true, m.owner);
    },
    /** Odpálí aktivní miny, ke kterým se přiblížil tank. Vrací, jestli něco vybuchlo. */
    checkMines(at: number): boolean {
      let any = false;
      for (const m of mines.slice()) {
        if (m.armedTurn > turn || !mines.includes(m)) continue;
        for (const tank of tanks) {
          if (!tank.alive) continue;
          const c = tankCenter(tank);
          if (Math.hypot(c.x - m.x, c.y - (m.y - 4)) < MINE_TRIGGER) {
            b.detonate(m, at);
            any = true;
            break;
          }
        }
      }
      return any;
    },
    /** Miny spadnou na nový povrch; ve vodě zaniknou. */
    settleMines(at: number) {
      for (const m of mines.slice()) {
        let y = m.y;
        while (y < WATER_Y && !terrain.solid(m.x, y + 1)) y++;
        if (y >= WATER_Y - 1) {
          mines.splice(mines.indexOf(m), 1);
          (ev.mines ??= []).push({ t: at, id: m.id, x: m.x, y, kind: 'gone' });
        } else m.y = y;
      }
    },
    placeMine(x: number, y: number, owner: string, at: number) {
      const id = mines.reduce((a, m) => Math.max(a, m.id), 0) + 1;
      const m: Mine = { id, x: Math.round(x), y: Math.round(y), owner, armedTurn: turn + 1 };
      mines.push(m);
      (ev.mines ??= []).push({ t: at, id, x: m.x, y: m.y, kind: 'placed' });
      // Příliš mnoho min → nejstarší zmizí
      while (mines.length > MAX_MINES) {
        const old = mines.shift()!;
        ev.mines.push({ t: at, id: old.id, x: old.x, y: old.y, kind: 'gone' });
      }
    },
  };
  return b;
}

interface Projectile {
  x: number;
  y: number;
  vx: number;
  vy: number;
  kind: ProjectileKind;
  bomblet: boolean;
  age: number;
  steps: number;
  alive: boolean;
  resting: boolean;
  /** fly = let, roll = válec na zemi, burn = kapka napalmu na zemi. */
  mode: 'fly' | 'roll' | 'burn' | 'drill' | 'walk';
  jumps: number;
  modeTime: number;
  rollDir: -1 | 1;
  speed: number;
  /** Nastřádaná vzdálenost k posunu o celé pixely (kutálení, tečení). */
  dist: number;
  stuck: number;
  bumps: number;
  traveled: number;
  lastCarve: number;
  hits: string[];
  track: { t0: number; weapon: ProjectileKind; bomblet?: boolean; pts: number[] };
}

/**
 * Simuluje výstřel od času `t0` (sekundy od začátku tahu). Mění terén i tanky
 * a vše zapisuje do `ev`. Vrací čas, kdy poslední projektil zanikl.
 * Deterministické: žádná náhoda, rozptyly potomků se odvozují z indexů.
 */
export function simulateShot(
  terrain: Terrain,
  tanks: Tank[],
  shooter: Tank,
  input: TurnInput,
  wind: number,
  t0: number,
  ev: TurnEvents,
  mines: Mine[] = [],
  turn = 0,
): number {
  const projectiles: Projectile[] = [];
  let t = t0;
  let lastEvent = t0;
  ev.teleports ??= [];

  const spawn = (x: number, y: number, vx: number, vy: number, kind: ProjectileKind, bomblet: boolean) => {
    const track = { t0: t, weapon: kind, pts: [round1(x), round1(y)] as number[], ...(bomblet ? { bomblet } : {}) };
    ev.projectiles.push(track);
    const p: Projectile = {
      x, y, vx, vy, kind, bomblet,
      age: 0, steps: 0, alive: true, resting: false,
      mode: 'fly', modeTime: 0, rollDir: vx < 0 ? -1 : 1, speed: 0, dist: 0, stuck: 0, bumps: 0,
      traveled: 0, lastCarve: -LASER_CARVE_EVERY, hits: [], jumps: 0,
      track,
    };
    projectiles.push(p);
    return p;
  };

  const kill = (p: Projectile) => {
    p.alive = false;
    p.track.pts.push(round1(p.x), round1(p.y));
    lastEvent = Math.max(lastEvent, t);
  };

  const blast = createBlaster(terrain, tanks, ev, mines, turn);
  const hurt = (tank: Tank, amount: number, at: number) => blast.hurt(tank, amount, at);
  const slide = (x: number, y: number, r: number, at = t) => blast.slide(x, y, r, at);
  const explode = (x: number, y: number, r: number, dmg: number, at = t, withSlide = true) =>
    blast.explode(x, y, r, dmg, at, withSlide);

  // --- Popáleniny od napalmu se střádají a vypisují po dávkách ---
  const burnAcc = new Map<string, number>();
  let lastFlush = t0;
  const flushBurns = (final: boolean) => {
    for (const [id, acc] of burnAcc) {
      const n = final ? Math.round(acc) : Math.floor(acc);
      if (n <= 0) continue;
      const tank = tanks.find((x) => x.id === id);
      if (tank) hurt(tank, n, t);
      burnAcc.set(id, acc - n);
      lastEvent = Math.max(lastEvent, t);
    }
    lastFlush = t;
  };

  const airstrike = (x: number) => {
    ev.explosions.push({ t, x: round1(x), y: round1(Math.min(WATER_Y, lastImpactY)), r: 0, marker: true });
    for (let i = 0; i < AIRSTRIKE_BOMBS; i++) {
      const off = i - (AIRSTRIKE_BOMBS - 1) / 2;
      spawn(x + off * AIRSTRIKE_SPACING, -80 - Math.abs(off) * 12, 0, 150, 'airbomb', true);
    }
  };

  const teleport = (x: number, y: number) => {
    const tx = Math.max(12, Math.min(WORLD_W - 12, Math.round(x)));
    let ty = Math.round(y);
    const from = { x: shooter.x, y: shooter.y };
    if (ty < WATER_Y) {
      // Vynořit se z terénu na povrch a usadit
      while (ty > 0 && terrain.solid(tx, ty - 1)) ty--;
      ty = settleY(terrain, tx, ty);
    }
    ty = Math.min(ty, WATER_Y);
    shooter.x = tx;
    shooter.y = ty;
    ev.teleports!.push({ t, id: shooter.id, fromX: from.x, fromY: from.y, toX: tx, toY: ty });
    lastEvent = Math.max(lastEvent, t + 0.6);
    if (ty >= WATER_Y && shooter.alive) {
      shooter.alive = false;
      ev.deaths.push({ t: t + 0.3, id: shooter.id, cause: 'water' });
    }
  };

  const quake = (p: Projectile) => {
    explode(p.x, p.y, WEAPONS.quake.radius, 10);
    (ev.quakes ??= []).push({ t, dur: QUAKE_DURATION });
    for (let i = 0; i < 8; i++) {
      const cx = 100 + i * 200;
      const cy = terrain.surfaceY(cx);
      if (cy < WATER_Y) slide(cx, cy, 90, t + 0.15 + i * 0.06);
    }
    for (const tank of tanks) if (tank.alive) hurt(tank, QUAKE_DAMAGE, t + 0.4);
    lastEvent = Math.max(lastEvent, t + QUAKE_DURATION);
  };

  const blackhole = (p: Projectile) => {
    const cx = p.x;
    const cy = p.y;
    (ev.blackholes ??= []).push({ t, x: round1(cx), y: round1(cy), dur: BLACKHOLE_DURATION });
    [20, 35, 50, 60].forEach((r, i) => {
      const op = makeOp(0, cx, cy, r);
      terrain.apply(op);
      ev.ops.push({ t: t + 0.2 + i * 0.45, op });
    });
    slide(cx, cy, 60, t + 1.7);
    for (const tank of tanks) {
      if (!tank.alive) continue;
      const c = tankCenter(tank);
      const d = Math.hypot(c.x - cx, c.y - cy);
      if (d >= BLACKHOLE_RANGE) continue;
      const move = d < 1 ? 0 : Math.min(d - 14, 30 + 120 * (1 - d / BLACKHOLE_RANGE));
      if (move > 0) {
        const tx = Math.round(c.x + ((cx - c.x) / d) * move);
        let ty = Math.round(c.y + ((cy - c.y) / d) * move + TANK_CENTER_DY);
        while (ty > 0 && terrain.solid(tx, ty - 1)) ty--;
        (ev.pulls ??= []).push({ t0: t + 0.2, t1: t + 1.6, id: tank.id, fromX: tank.x, fromY: tank.y, toX: tx, toY: ty });
        tank.x = tx;
        tank.y = ty;
      }
      hurt(tank, Math.max(1, Math.round(WEAPONS.blackhole.damage * (1 - d / 260))), t + 1.2);
    }
    lastEvent = Math.max(lastEvent, t + BLACKHOLE_DURATION);
  };

  const lightning = (x: number) => {
    const at = t + 0.3;
    ev.explosions.push({ t, x: round1(x), y: round1(Math.min(WATER_Y, lastImpactY)), r: 0, marker: true });
    const top = terrain.surfaceY(x);
    let y1 = Math.min(top, WATER_Y);
    for (const tank of tanks) {
      if (!tank.alive || Math.abs(tank.x - x) >= LIGHTNING_WIDTH) continue;
      hurt(tank, WEAPONS.lightning.damage, at);
      y1 = Math.max(y1, tank.y);
      explode(tank.x, tank.y - 6, 10, 0, at, false);
    }
    if (top < WATER_Y) explode(x, top, WEAPONS.lightning.radius, 0, at, true);
    (ev.lightnings ??= []).push({ t: at, x: round1(x), y0: -60, y1: round1(y1) });
    lastEvent = Math.max(lastEvent, at + 0.5);
  };

  let lastImpactY = 0;
  const impact = (p: Projectile) => {
    lastImpactY = p.y;
    switch (p.kind) {
      case 'shell':
      case 'heavy':
      case 'grenade':
      case 'nuke':
      case 'roller':
      case 'guided':
      case 'bunker':
      case 'sheep':
      case 'bazuka':
      case 'bomber':
        explode(p.x, p.y, WEAPONS[p.kind].radius, WEAPONS[p.kind].damage);
        break;
      case 'airbomb':
        explode(p.x, p.y, WEAPONS.airstrike.radius, WEAPONS.airstrike.damage);
        break;
      case 'cluster': {
        const w = WEAPONS.cluster;
        // Nerozpadlá kazeta vybuchne jako malá střela
        explode(p.x, p.y, p.bomblet ? w.radius : 20, p.bomblet ? w.damage : 20);
        break;
      }
      case 'banana':
        if (p.bomblet) {
          explode(p.x, p.y, BANANA_RADIUS, BANANA_DAMAGE);
        } else {
          explode(p.x, p.y, WEAPONS.banana.radius, WEAPONS.banana.damage);
          for (let i = 0; i < BANANA_COUNT; i++) {
            const off = i - (BANANA_COUNT - 1) / 2;
            spawn(p.x, p.y - 6, off * 85, -240 - Math.abs(off) * 25, 'banana', true);
          }
        }
        break;
      case 'napalm':
        explode(p.x, p.y, WEAPONS.napalm.radius, WEAPONS.napalm.damage);
        for (let i = 0; i < NAPALM_FLAMES; i++) {
          const off = i - (NAPALM_FLAMES - 1) / 2;
          spawn(p.x, p.y - 4, off * 35, -110 - (i % 3) * 30, 'flame', true);
        }
        break;
      case 'airstrike':
        airstrike(p.x);
        break;
      case 'pellet': {
        const dmg = Math.round(WEAPONS.shotgun.damage * Math.max(0.3, 1 - p.traveled / 700));
        explode(p.x, p.y, WEAPONS.shotgun.radius, dmg, t, false);
        break;
      }
      case 'mine':
        blast.placeMine(p.x, p.y, shooter.id, t);
        break;
      case 'quake':
        quake(p);
        break;
      case 'blackhole':
        blackhole(p);
        break;
      case 'lightning':
        lightning(p.x);
        break;
      case 'shotgun':
        break;
      case 'teleport':
        teleport(p.x, p.y);
        break;
      case 'digger': {
        const w = WEAPONS.digger;
        const n = Math.hypot(p.vx, p.vy) || 1;
        const dx = p.vx / n;
        const dy = p.vy / n;
        for (let i = 0; i < DIGGER_STEPS; i++) {
          explode(p.x + dx * DIGGER_SPACING * i, p.y + dy * DIGGER_SPACING * i, w.radius, w.damage, t + i * 0.07, false);
        }
        // Jeden společný sesuv přes celý tunel
        const half = (DIGGER_SPACING * (DIGGER_STEPS - 1)) / 2;
        slide(p.x + dx * half, p.y + dy * half, w.radius + half, t + (DIGGER_STEPS - 1) * 0.07);
        break;
      }
      case 'dirt': {
        const w = WEAPONS.dirt;
        const op = makeOp(1, p.x, p.y, w.radius);
        terrain.apply(op);
        ev.ops.push({ t, op });
        ev.explosions.push({ t, x: op[1], y: op[2], r: op[3], dirt: true });
        lastEvent = Math.max(lastEvent, t);
        slide(p.x, p.y, w.radius);
        break;
      }
      case 'laser':
      case 'flame':
        break;
    }
    kill(p);
  };

  /** Poloha těsně nad zemí po dopadu na (x, y): vystoupat z pevného terénu. */
  const surfaceAt = (p: Projectile, x: number, y: number) => {
    p.x = Math.round(x);
    p.y = Math.round(y);
    let guard = 0;
    while (terrain.solid(p.x, p.y) && guard++ < 40) p.y--;
    p.vx = 0;
    p.vy = 0;
  };

  const splash = (p: Projectile, x: number) => {
    p.x = x;
    p.y = WATER_Y;
    ev.splashes.push({ t, x: round1(x) });
    kill(p);
  };

  const tankNear = (x: number, y: number, r: number, skipShooter: boolean) => {
    for (const tank of tanks) {
      if (!tank.alive || (skipShooter && tank === shooter)) continue;
      const c = tankCenter(tank);
      if (Math.hypot(c.x - x, c.y - y) < r) return tank;
    }
    return null;
  };

  const finish = () => {
    blast.checkMines(t);
    blast.settleMines(t);
    return Math.max(lastEvent, blast.last);
  };

  // Tank dojel k aktivní mině → vybuchne dřív, než vystřelí
  blast.checkMines(t0);
  if (!shooter.alive) return finish();

  if (input.weapon === 'guided') {
    // Řízená střela: přehrát zaznamenané řízení stejným kódem jako klient
    const flight = new GuidedFlight(terrain, tanks, shooter, input.angle);
    const read = steerReader(input.steer);
    const track = { t0: t, weapon: 'guided' as ProjectileKind, pts: [round1(flight.x), round1(flight.y)] };
    ev.projectiles.push(track);
    while (flight.status === 'flying') {
      flight.step(read());
      t = t0 + flight.steps * SIM_DT;
      if (flight.steps % FRAME_EVERY === 0) track.pts.push(round1(flight.x), round1(flight.y));
    }
    track.pts.push(round1(flight.x), round1(flight.y));
    lastEvent = Math.max(lastEvent, t);
    if (flight.status === 'hit') explode(flight.x, flight.y, WEAPONS.guided.radius, WEAPONS.guided.damage);
    else if (flight.status === 'water') ev.splashes.push({ t, x: round1(flight.x) });
    return finish();
  }

  if (input.weapon === 'kamikaze') {
    // Kamikadze dron: přehrát záznam řízení; konec záznamu = odpálení
    const flight = new KamikazeFlight(terrain, tanks, shooter, input.angle, wind);
    const read = steerRuns(input.steer);
    const track = { t0: t, weapon: 'kamikaze' as ProjectileKind, pts: [round1(flight.x), round1(flight.y)], z: [round1(flight.z)] };
    ev.projectiles.push(track);
    while (flight.status === 'flying') {
      const code = read();
      if (code === null) {
        flight.detonate();
        break;
      }
      flight.step(code);
      t = t0 + flight.steps * SIM_DT;
      if (flight.steps % FRAME_EVERY === 0) {
        track.pts.push(round1(flight.x), round1(flight.y));
        track.z.push(round1(flight.z));
      }
    }
    track.pts.push(round1(flight.x), round1(flight.y));
    track.z.push(round1(flight.z));
    lastEvent = Math.max(lastEvent, t);
    if (flight.status === 'hit') blast.explode(flight.x, flight.y, WEAPONS.kamikaze.radius, WEAPONS.kamikaze.damage, t, true, undefined, flight.z);
    else if (flight.status === 'water') ev.splashes.push({ t, x: round1(flight.x) });
    return finish();
  }

  const center = tankCenter(shooter);
  /** Ústí hlavně může být ve svahu – prvních pár pixelů střela terénem projde. */
  const clearOfBarrel = (p: Projectile) => p.bomblet || Math.hypot(p.x - center.x, p.y - center.y) > BARREL_LEN + 10;

  // ---------- Režimy pohybu ----------

  const stepLaser = (p: Projectile) => {
    const nx = p.x + p.vx * SIM_DT;
    const ny = p.y + p.vy * SIM_DT;
    p.traveled += LASER_SPEED * SIM_DT;
    if (nx < 0 || nx > WORLD_W || ny < -200 || p.traveled > LASER_RANGE) {
      p.x = nx;
      p.y = ny;
      kill(p);
      return;
    }
    if (ny >= WATER_Y) {
      splash(p, nx);
      return;
    }
    p.x = nx;
    p.y = ny;
    if (terrain.solid(nx, ny) && p.traveled - p.lastCarve >= LASER_CARVE_EVERY) {
      const op = makeOp(0, nx, ny, WEAPONS.laser.radius);
      terrain.apply(op);
      ev.ops.push({ t, op });
      p.lastCarve = p.traveled;
    }
    for (const tank of tanks) {
      if (!tank.alive || tank === shooter || p.hits.includes(tank.id)) continue;
      const c = tankCenter(tank);
      if (Math.hypot(c.x - nx, c.y - ny) < TANK_HIT_R + 2) {
        p.hits.push(tank.id);
        hurt(tank, WEAPONS.laser.damage, t);
        ev.explosions.push({ t, x: round1(nx), y: round1(ny), r: 8 });
      }
    }
  };

  const stepFly = (p: Projectile) => {
    if (p.kind === 'laser') return stepLaser(p);
    if (p.resting) {
      if (!terrain.solid(p.x, p.y + 1)) p.resting = false;
      return;
    }
    const pellet = p.kind === 'pellet';
    if (pellet && p.age > SHOTGUN_LIFE) return kill(p);
    const prevVy = p.vy;
    if (!pellet) p.vx += wind * WIND_ACCEL * SIM_DT;
    p.vy += terrain.gravity * SIM_DT * (pellet ? 0.25 : p.kind === 'bazuka' ? 0.6 : 1);
    const nx = p.x + p.vx * SIM_DT;
    const ny = p.y + p.vy * SIM_DT;
    if (pellet) p.traveled += Math.hypot(nx - p.x, ny - p.y);

    if (nx < -60 || nx > WORLD_W + 60) {
      p.x = nx;
      p.y = ny;
      kill(p);
      return;
    }
    if (ny >= WATER_Y) {
      if (p.kind === 'airstrike' || p.kind === 'teleport' || p.kind === 'lightning') {
        p.x = nx;
        p.y = WATER_Y;
        lastImpactY = WATER_Y;
        if (p.kind === 'airstrike') airstrike(nx);
        else if (p.kind === 'lightning') lightning(nx);
        else teleport(nx, WATER_Y);
        kill(p);
      } else splash(p, nx);
      return;
    }

    if (p.kind === 'cluster' && !p.bomblet && prevVy < 0 && p.vy >= 0) {
      p.x = nx;
      p.y = ny;
      kill(p);
      for (let i = 0; i < CLUSTER_COUNT; i++) {
        const off = i - (CLUSTER_COUNT - 1) / 2;
        spawn(p.x, p.y, p.vx + off * 55, p.vy - 30 + Math.abs(off) * 10, 'cluster', true);
      }
      return;
    }

    const passesTanks = p.kind === 'grenade' || p.kind === 'dirt' || p.kind === 'flame' || p.kind === 'mine';
    if (!passesTanks && hitsTank(tanks, shooter, p, nx, ny)) {
      p.x = nx;
      p.y = ny;
      impact(p);
      return;
    }

    if (terrain.solid(nx, ny) && clearOfBarrel(p)) {
      if (p.kind === 'grenade') {
        bounce(terrain, p, nx, ny);
      } else if (p.kind === 'flame') {
        surfaceAt(p, nx, ny);
        p.mode = 'burn';
      } else if (p.kind === 'roller') {
        surfaceAt(p, nx, ny);
        p.mode = 'roll';
        p.speed = Math.max(60, Math.min(200, Math.abs(p.vx) + 40));
      } else if (p.kind === 'bunker') {
        const n = Math.hypot(p.vx, p.vy) || 1;
        p.vx = (p.vx / n) * BUNKER_SPEED;
        p.vy = (p.vy / n) * BUNKER_SPEED;
        p.x = nx;
        p.y = ny;
        p.mode = 'drill';
        p.traveled = 0;
        p.lastCarve = 0;
      } else if (p.kind === 'sheep') {
        p.rollDir = p.vx < 0 ? -1 : 1;
        surfaceAt(p, nx, ny);
        p.mode = 'walk';
      } else if (p.kind === 'mine') {
        surfaceAt(p, nx, ny);
        impact(p);
      } else {
        p.x = nx;
        p.y = ny;
        impact(p);
      }
    } else {
      p.x = nx;
      p.y = ny;
    }
  };

  /** Pád bez opory (válec i kapka); vrací true, když objekt padá. */
  const fallIfUnsupported = (p: Projectile) => {
    if (terrain.solid(p.x, p.y + 1)) {
      p.vy = 0;
      return false;
    }
    p.vy += terrain.gravity * SIM_DT;
    const ny = p.y + p.vy * SIM_DT;
    const nx = p.x + (p.mode === 'roll' ? p.rollDir * p.speed * 0.5 * SIM_DT : 0);
    if (ny >= WATER_Y) {
      splash(p, nx);
      return true;
    }
    if (terrain.solid(nx, ny)) {
      surfaceAt(p, nx, ny);
    } else {
      p.x = nx;
      p.y = ny;
    }
    return true;
  };

  const stepRoll = (p: Projectile) => {
    p.modeTime += SIM_DT;
    if (p.modeTime >= ROLLER_TIME) return impact(p);
    if (fallIfUnsupported(p) || !p.alive) return;
    p.dist += p.speed * SIM_DT;
    while (p.dist >= 1 && p.alive) {
      p.dist -= 1;
      const nx = p.x + p.rollDir;
      let ny = p.y;
      if (terrain.solid(nx, p.y)) {
        // Do kopce: max. 2 px, jinak se odrazí
        let k = 1;
        while (k <= 2 && terrain.solid(nx, p.y - k)) k++;
        if (k > 2) {
          p.bumps++;
          p.rollDir = p.rollDir === 1 ? -1 : 1;
          p.speed *= 0.5;
          if (p.bumps >= 2) return impact(p);
          break;
        }
        ny = p.y - k;
        p.speed -= 6;
      } else if (!terrain.solid(nx, p.y + 1)) {
        // Z kopce (max. 3 px na krok), jinak přes hranu – pak spadne
        let d = 1;
        while (d <= 3 && !terrain.solid(nx, p.y + d + 1)) d++;
        if (d <= 3) {
          ny = p.y + d;
          p.speed += 5;
        }
      }
      p.x = nx;
      p.y = ny;
      if (nx < 2 || nx > WORLD_W - 2) return kill(p);
      if (tankNear(p.x, p.y - 3, TANK_HIT_R + 2, p.modeTime < 0.3)) return impact(p);
    }
    p.speed = Math.max(0, Math.min(260, p.speed - 20 * SIM_DT));
    p.stuck = p.speed < 40 ? p.stuck + SIM_DT : 0;
    if (p.stuck > 0.5) impact(p);
  };

  const stepDrill = (p: Projectile) => {
    const nx = p.x + p.vx * SIM_DT;
    const ny = p.y + p.vy * SIM_DT;
    if (nx < 0 || nx > WORLD_W || ny < -200) {
      p.x = nx;
      p.y = ny;
      return kill(p);
    }
    if (ny >= WATER_Y) return splash(p, nx);
    p.x = nx;
    p.y = ny;
    p.traveled += BUNKER_SPEED * SIM_DT;
    if (p.traveled - p.lastCarve >= 10) {
      const op = makeOp(0, nx, ny, 4);
      terrain.apply(op);
      ev.ops.push({ t, op });
      p.lastCarve = p.traveled;
    }
    if (tankNear(nx, ny, TANK_HIT_R, false) || p.traveled >= BUNKER_DEPTH) impact(p);
  };

  const stepWalk = (p: Projectile) => {
    p.modeTime += SIM_DT;
    if (p.modeTime >= SHEEP_TIME) return impact(p);
    const airborne = p.vy !== 0 || !terrain.solid(p.x, p.y + 1);
    if (airborne) {
      p.vy += terrain.gravity * SIM_DT;
      if (p.vy === 0) p.vy = 0.001;
      let nx = p.x + p.vx * SIM_DT;
      const ny = p.y + p.vy * SIM_DT;
      if (nx < 3 || nx > WORLD_W - 3) {
        p.vx = -p.vx;
        p.rollDir = p.rollDir === 1 ? -1 : 1;
        nx = p.x;
      }
      if (ny >= WATER_Y) return splash(p, nx);
      if (terrain.solid(nx, ny)) surfaceAt(p, nx, ny);
      else {
        p.x = nx;
        p.y = ny;
      }
      if (tankNear(p.x, p.y - 4, TANK_HIT_R + 3, p.modeTime < 0.3)) impact(p);
      return;
    }
    p.dist += SHEEP_SPEED * SIM_DT;
    while (p.dist >= 1 && p.alive) {
      p.dist -= 1;
      const nx = p.x + p.rollDir;
      if (nx < 3 || nx > WORLD_W - 3) {
        p.rollDir = p.rollDir === 1 ? -1 : 1;
        break;
      }
      if (terrain.solid(nx, p.y)) {
        let k = 1;
        while (k <= 6 && terrain.solid(nx, p.y - k)) k++;
        if (k > 6) {
          // Zeď: skok, a když nepomůže, otočka
          if (p.jumps < 3) {
            p.jumps++;
            p.vy = -230;
            p.vx = p.rollDir * 90;
            p.y -= 1;
          } else {
            p.rollDir = p.rollDir === 1 ? -1 : 1;
            p.bumps++;
            if (p.bumps >= 3) return impact(p);
          }
          break;
        }
        p.x = nx;
        p.y -= k;
      } else if (!terrain.solid(nx, p.y + 1)) {
        let d = 1;
        while (d <= 6 && !terrain.solid(nx, p.y + d + 1)) d++;
        p.x = nx;
        if (d <= 6) p.y += d;
        else {
          // Přes hranu – dál letí
          p.vx = p.rollDir * SHEEP_SPEED;
          p.vy = 1;
          break;
        }
      } else p.x = nx;
      if (tankNear(p.x, p.y - 4, TANK_HIT_R + 3, p.modeTime < 0.3)) return impact(p);
    }
  };

  const stepBurn = (p: Projectile) => {
    p.modeTime += SIM_DT;
    if (p.modeTime >= NAPALM_BURN) return kill(p);
    if (fallIfUnsupported(p) || !p.alive) return;
    // Teče z kopce: posun k nižšímu sousedovi
    p.dist += NAPALM_FLOW * SIM_DT;
    while (p.dist >= 1) {
      p.dist -= 1;
      const ground = (x: number) => {
        if (terrain.solid(x, p.y)) return -1; // zeď
        let y = p.y;
        while (y < p.y + 8 && !terrain.solid(x, y + 1)) y++;
        return y;
      };
      const l = ground(p.x - 1);
      const r = ground(p.x + 1);
      const best = Math.max(l, r);
      if (best <= p.y) break; // louže – dál neteče
      const dir = r > l || (r === l && (p.steps & 1)) ? 1 : -1;
      p.x += dir;
      p.y = dir === 1 ? r : l;
    }
    for (const tank of tanks) {
      if (!tank.alive) continue;
      const c = tankCenter(tank);
      if (Math.hypot(c.x - p.x, c.y - p.y) < FLAME_R) burnAcc.set(tank.id, (burnAcc.get(tank.id) ?? 0) + NAPALM_DPS * SIM_DT);
    }
    lastEvent = Math.max(lastEvent, t);
  };

  const stepProjectile = (p: Projectile) => {
    if (!p.alive) return;
    p.age += SIM_DT;
    p.steps++;

    if (p.kind === 'grenade' && p.age >= GRENADE_FUSE) {
      impact(p);
      return;
    }
    if (p.mode === 'roll') stepRoll(p);
    else if (p.mode === 'burn') stepBurn(p);
    else if (p.mode === 'drill') stepDrill(p);
    else if (p.mode === 'walk') stepWalk(p);
    else stepFly(p);

    if (p.alive && p.steps % FRAME_EVERY === 0) p.track.pts.push(round1(p.x), round1(p.y));
  };

  if (input.weapon === 'bomber') {
    const flight = new BomberFlight(input.angle);
    const read = steerRuns(input.steer);
    const planeTrack = { t0: t, weapon: 'bomberplane' as ProjectileKind, pts: [round1(flight.x), round1(flight.y)] };
    ev.projectiles.push(planeTrack);

    while (flight.status === 'flying') {
      const code = read();
      const dropped = flight.step(code === 1);
      t = t0 + flight.steps * SIM_DT;
      if (flight.steps % FRAME_EVERY === 0) planeTrack.pts.push(round1(flight.x), round1(flight.y));

      if (dropped) {
        const b = flight.droppedBombs[flight.droppedBombs.length - 1];
        spawn(b.x, b.y, b.vx, b.vy, 'bomber', false);
      }

      for (const p of projectiles.slice()) {
        stepProjectile(p);
      }
      if (t - lastFlush >= 0.5) flushBurns(false);
    }
    planeTrack.pts.push(round1(flight.x), round1(flight.y));
    lastEvent = Math.max(lastEvent, t);

    let step = flight.steps;
    while (projectiles.some((p) => p.alive) && t - t0 < MAX_SHOT_TIME) {
      step++;
      t = t0 + step * SIM_DT;
      for (const p of projectiles.slice()) {
        stepProjectile(p);
      }
      if (t - lastFlush >= 0.5) flushBurns(false);
    }
    for (const p of projectiles) if (p.alive) kill(p);
    flushBurns(true);
    return finish();
  }

  const tip = barrelTip(shooter, input.angle);
  const a = (input.angle * Math.PI) / 180;
  const speed = input.weapon === 'laser' ? LASER_SPEED : input.weapon === 'bazuka' ? (input.power / 100) * MAX_SPEED * 1.3 : (input.power / 100) * MAX_SPEED;
  if (input.weapon === 'shotgun') {
    const sp = 700 + input.power * 5;
    const half = (SHOTGUN_PELLETS - 1) / 2;
    for (let i = 0; i < SHOTGUN_PELLETS; i++) {
      const aa = ((input.angle + ((i - half) / half) * SHOTGUN_SPREAD) * Math.PI) / 180;
      spawn(tip.x, tip.y, Math.cos(aa) * sp, -Math.sin(aa) * sp, 'pellet', false);
    }
  } else {
    spawn(tip.x, tip.y, Math.cos(a) * speed, -Math.sin(a) * speed, input.weapon, false);
  }

  // Tank zakopaný v zemi → okamžitý dopad u hlavně (laser se provrtá sám)
  if (input.weapon !== 'laser' && terrain.solid(center.x, center.y - 3)) {
    impact(projectiles[0]);
  }

  let step = 0;
  while (projectiles.some((p) => p.alive) && t - t0 < MAX_SHOT_TIME) {
    step++;
    t = t0 + step * SIM_DT;
    // Pozor: spawn během iterace přidává prvky – iterujeme přes kopii
    for (const p of projectiles.slice()) {
      stepProjectile(p);
    }
    if (t - lastFlush >= 0.5) flushBurns(false);
  }
  for (const p of projectiles) if (p.alive) kill(p);
  flushBurns(true);
  return finish();
}

function hitsTank(tanks: Tank[], shooter: Tank, p: Projectile, x: number, y: number): boolean {
  for (const tank of tanks) {
    if (!tank.alive) continue;
    if (tank === shooter && p.age < 0.1 && !p.bomblet) continue;
    const c = tankCenter(tank);
    if (Math.hypot(c.x - x, c.y - y) < TANK_HIT_R) return true;
  }
  return false;
}

function bounce(terrain: Terrain, p: Projectile, hx: number, hy: number) {
  let nx = 0;
  let ny = 0;
  for (let dy = -4; dy <= 4; dy++) {
    for (let dx = -4; dx <= 4; dx++) {
      if (terrain.solid(hx + dx, hy + dy)) {
        nx -= dx;
        ny -= dy;
      }
    }
  }
  const n = Math.hypot(nx, ny);
  if (n === 0) {
    nx = 0;
    ny = -1;
  } else {
    nx /= n;
    ny /= n;
  }
  const d = p.vx * nx + p.vy * ny;
  if (d < 0) {
    p.vx -= 2 * d * nx;
    p.vy -= 2 * d * ny;
  }
  p.vx *= 0.5;
  p.vy *= 0.5;
  // Vytlačit ze země, pokud jsme uvízli
  let guard = 0;
  while (terrain.solid(p.x, p.y) && guard++ < 10) {
    p.x += nx;
    p.y += ny;
  }
  if (Math.hypot(p.vx, p.vy) < 25) {
    p.vx = 0;
    p.vy = 0;
    p.resting = true;
  }
}

/** Po výstřelu nechá všechny tanky spadnout, spočítá zranění z pádu a utopení. */
export function settleTanks(terrain: Terrain, tanks: Tank[], t: number, ev: TurnEvents): boolean {
  let any = false;
  for (const tank of tanks) {
    if (!tank.alive) continue;
    const toY = settleY(terrain, tank.x, tank.y);
    if (toY === tank.y) continue;
    any = true;
    const dist = toY - tank.y;
    const drowned = toY >= WATER_Y;
    const damage = drowned ? 0 : dist > FALL_SAFE ? Math.round((dist - FALL_SAFE) * FALL_DMG_PER_PX) : 0;
    ev.falls.push({ t, id: tank.id, fromY: tank.y, toY, damage });
    const landed = t + FALL_DURATION;
    tank.y = toY;
    if (drowned) {
      tank.alive = false;
      ev.deaths.push({ t: landed, id: tank.id, cause: 'water' });
    } else if (damage > 0) {
      tank.hp = Math.max(0, tank.hp - damage);
      ev.damage.push({ t: landed, id: tank.id, amount: damage });
      if (tank.hp <= 0) {
        tank.alive = false;
        ev.deaths.push({ t: landed, id: tank.id, cause: 'hp' });
      }
    }
  }
  return any;
}

function round1(v: number) {
  return Math.round(v * 10) / 10;
}
